import { api } from './api';
import {
  momentsAnalyticsSessionClient,
  momentsTokenClient,
} from './api/momentsApi';
import { getMomentsGeneration } from './momentsPrivateBoundary';
import {
  clearMomentsPrivateState,
  failMomentsReadiness,
  observeMomentsAuthRealm,
  observeMomentsSubject,
} from './slices/appSlice';

import type { Middleware } from '@reduxjs/toolkit';

export const MOMENTS_BOUNDARY_STORAGE_KEY = 'moments-private-boundary-nonce';

// The shared RTK Query cache includes notes/artifacts/flags and authenticated
// identity. Reset it at a real boundary, not at routine flag refetch/revocation.
// No private data is put in storage: only a nonprivate cross-tab boundary nonce.
export const momentsPrivateMiddleware: Middleware =
  (store) => (next) => (action) => {
    // Only a currently accepted identity query may change the auth realm. A
    // response removed by reset must not re-enroll an old subject.
    if (
      action &&
      typeof action === 'object' &&
      'type' in action &&
      action.type === `${api.reducerPath}/executeQuery/fulfilled`
    ) {
      const identity = action as {
        meta?: {
          requestId?: string;
          arg?: { endpointName?: string; queryCacheKey?: string };
        };
        payload?: Record<string, unknown>;
      };
      const state = store.getState() as {
        api?: { queries?: Record<string, { requestId?: string }> };
      };
      const key = identity.meta?.arg?.queryCacheKey;
      if (
        identity.meta?.arg?.endpointName === 'getCurrentUser' &&
        key &&
        state.api?.queries?.[key]?.requestId === identity.meta.requestId &&
        identity.payload
      ) {
        const user = identity.payload;
        const organization =
          user.organization &&
          typeof user.organization === 'object' &&
          'id' in user.organization
            ? user.organization.id
            : undefined;
        store.dispatch(
          observeMomentsAuthRealm(
            JSON.stringify([
              user.id,
              user.roles,
              user.organizationId,
              organization,
              user.momentsBaseUrl,
            ]),
          ),
        );
      }
    }
    if (
      action &&
      typeof action === 'object' &&
      'type' in action &&
      action.type === `${api.reducerPath}/executeQuery/rejected`
    ) {
      const rejected = action as {
        meta?: {
          requestId?: string;
          arg?: { endpointName?: string; queryCacheKey?: string };
        };
        payload?: { status?: unknown };
      };
      const state = store.getState() as {
        app?: { momentsAuthRealm?: string; momentsSubject?: string };
        api?: { queries?: Record<string, { requestId?: string }> };
      };
      const key = rejected.meta?.arg?.queryCacheKey;
      if (
        key &&
        state.api?.queries?.[key]?.requestId === rejected.meta?.requestId &&
        rejected.payload?.status === 401 &&
        ['getCurrentUser', 'getAuthStatus'].includes(
          rejected.meta?.arg?.endpointName ?? '',
        ) &&
        (state.app?.momentsAuthRealm || state.app?.momentsSubject)
      ) {
        store.dispatch(clearMomentsPrivateState());
      }
    }
    const before = getMomentsGeneration(store.getState());
    const result = next(action);
    const after = getMomentsGeneration(store.getState());
    if (before !== after) {
      momentsTokenClient.clear();
      momentsAnalyticsSessionClient.clear();
      store.dispatch(api.util.resetApiState());
      if (
        (clearMomentsPrivateState.match(action) ||
          failMomentsReadiness.match(action) ||
          observeMomentsSubject.match(action) ||
          observeMomentsAuthRealm.match(action)) &&
        typeof window !== 'undefined'
      ) {
        try {
          window.localStorage.setItem(
            MOMENTS_BOUNDARY_STORAGE_KEY,
            crypto.randomUUID(),
          );
        } catch {
          /* Focus refetch independently reconciles tabs when storage is unavailable. */
        }
      }
    }
    return result;
  };
